import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import {
  FiCalendar,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import moment from 'moment-timezone';
import styles from './SingleDatePicker.module.scss';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function getSubLabel(dateStr) {
  const m = moment(dateStr, 'YYYY-MM-DD');
  if (m.isSame(moment(), 'day')) return 'Today';
  if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
  return m.format('dddd');
}

function buildCalendarGrid(year, month) {
  const start = moment({ year, month }).startOf('week');
  return Array.from({ length: 42 }, (_, i) => start.clone().add(i, 'days'));
}

export const SingleDatePicker = ({ date, onDateChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selected = useMemo(() => moment(date, 'YYYY-MM-DD'), [date]);
  const [viewYear, setViewYear] = useState(selected.year());
  const [viewMonth, setViewMonth] = useState(selected.month());
  const containerRef = useRef(null);
  const today = useMemo(() => moment().startOf('day'), []);

  useEffect(() => {
    if (!isOpen) return;
    const onOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onOutsideClick);
    return () => document.removeEventListener('mousedown', onOutsideClick);
  }, [isOpen]);

  // Sync view when date prop changes externally
  useEffect(() => {
    setViewYear(selected.year());
    setViewMonth(selected.month());
  }, [selected]);

  const days = useMemo(
    () => buildCalendarGrid(viewYear, viewMonth),
    [viewYear, viewMonth]
  );

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else setViewMonth((m) => m + 1);
  };

  const handleDayClick = useCallback(
    (day) => {
      if (day.isAfter(today)) return;
      onDateChange(day.format('YYYY-MM-DD'));
      setIsOpen(false);
    },
    [onDateChange, today]
  );

  const handlePreset = useCallback(
    (dayMoment) => {
      onDateChange(dayMoment.format('YYYY-MM-DD'));
      setIsOpen(false);
    },
    [onDateChange]
  );

  return (
    <div className={styles.wrapper} ref={containerRef}>
      <div
        className={styles.trigger}
        onClick={() => setIsOpen((o) => !o)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setIsOpen((o) => !o)}
      >
        <FiCalendar size={20} className={styles.calendarIcon} />
        <div className={styles.textGroup}>
          <span className={styles.dateLabel}>Date</span>
          <span className={styles.dateText}>
            {selected.format('MMM DD, YYYY')}
          </span>
        </div>
        <FiChevronDown
          size={14}
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
        />
      </div>

      {isOpen && (
        <div className={styles.dropdown}>
          {/* Month navigation */}
          <div className={styles.calHeader}>
            <button className={styles.navBtn} onClick={prevMonth} type="button">
              <FiChevronLeft size={13} />
            </button>
            <span className={styles.monthLabel}>
              {moment({ year: viewYear, month: viewMonth }).format('MMMM YYYY')}
            </span>
            <button className={styles.navBtn} onClick={nextMonth} type="button">
              <FiChevronRight size={13} />
            </button>
          </div>

          {/* Weekday headers */}
          <div className={styles.weekdays}>
            {WEEKDAYS.map((d) => (
              <span key={d} className={styles.weekday}>
                {d}
              </span>
            ))}
          </div>

          {/* Day grid */}
          <div className={styles.daysGrid}>
            {days.map((day, i) => {
              const isCurrentMonth = day.month() === viewMonth;
              const isSelected = day.isSame(selected, 'day');
              const isToday = day.isSame(today, 'day');
              const isFuture = day.isAfter(today);

              let cls = styles.day;
              if (!isCurrentMonth) cls += ` ${styles.dayOtherMonth}`;
              if (isSelected) cls += ` ${styles.daySelected}`;
              else if (isToday) cls += ` ${styles.dayToday}`;
              if (isFuture) cls += ` ${styles.dayDisabled}`;

              return (
                <button
                  key={i}
                  type="button"
                  className={cls}
                  onClick={() => handleDayClick(day)}
                  disabled={isFuture}
                >
                  {day.date()}
                </button>
              );
            })}
          </div>

          {/* Quick presets */}
          <div className={styles.presets}>
            <button
              type="button"
              className={styles.preset}
              onClick={() => handlePreset(moment())}
            >
              Today
            </button>
            <button
              type="button"
              className={styles.preset}
              onClick={() => handlePreset(moment().subtract(1, 'day'))}
            >
              Yesterday
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
